import { useContext, useState, useEffect } from 'react'
import WaveDivider from './WaveDivider'
import BlogHero from './BlogHero'
import Breadcrumb from './Breadcrumb'
import {
  Card,
  FaqGrid,
  Wrapper,
  BreadCrumbWrapper,
  SupportContainer,
  SupportLabel,
  SupportDescription,
  BlackCard,
  YellowCard,
  SupportContact,
  SupportContactContainer,
  SupportSectionLeft,
  SupportSectionRight,
  ContactNumber,
  ContactName,
  ButtonContainer,
  Description,
  FaqHeader,
} from './HelpCentrePageTemplate.styles'
import Icon, { IconName } from './Icons'
import { Spacing, Heading, Accordion, Button } from '@big-red-group/storefront-common-ui'
import Head from 'next/head'
import { TOP_BAR_HEIGHT_DESKTOP } from '@/constant/ui'
import faqs from '../json/helpCentreFaqs.json'
import virginfaqs from '../json/virginHelpCentreFaqs.json'
import { initChat } from '@/helpers/LiveChatHelper'
import { createPageTitle } from '@/helpers/PageTitleHelper'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { getIndexTag } from '@/helpers/SeoHelper'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { AccordionLabel } from './AccordionLabel.styles'
import { RichSnippetContext } from '@/context/RichSnippetContext'
type FaqTopic = {
  icon: IconName
  label: string
  description: string
  faqs?: Faq[]
}

type Faq = {
  question: string
  answer: string
}

export const getFlattenedFaqs = (handlerKey: string | undefined) => {
  const faqTopics = handlerKey === 'virgin' ? (virginfaqs as FaqTopic[]) : (faqs as FaqTopic[])
  const baseUrl = getUrlPrefix(handlerKey)
  formatFaqTopics(faqTopics, baseUrl)

  return {
    faqTopics,
    faqs: faqTopics.flatMap((c) => (c.faqs || []).flatMap((q) => ({ question: q.question, answer: q.answer }))),
  }
}

const formatFaqTopics = (faqTopics: FaqTopic[], baseUrl: string) => {
  faqTopics.forEach((topics) => {
    topics.faqs!.forEach((faq) => {
      if (faq.question === 'How do I request a refund?') {
        faq.answer = `If you wish to request a refund for your purchase, please visit our <a href="${baseUrl}/en/request-refund">Request a Refund page.</a>`
      }
    })
  })
}

const HelpCentrePageTemplate = () => {
  const defaultProperties = useContext(DefaultPropertiesContext)
  const handlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const partnerCode = defaultProperties?.agentConfiguration?.partnerCode
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent

  const { faqTopics, faqs } = getFlattenedFaqs(handlerKey)

  const heroStyle = { background: '#fac532' }

  const breadCrumbData = [
    { text: 'Home', href: '/en' },
    { text: 'Help Centre', href: '/en/faqs' },
  ]
  // FIXME: Hacky fix for VA refund link prefix, needs to be reworked

  const handleClickFaq = (scrollId: string) => {
    const element = document.getElementById(scrollId)
    if (element) {
      const scrollPaddingTop = 20
      const scrollPoint =
        element.getBoundingClientRect().top + window.scrollY - (TOP_BAR_HEIGHT_DESKTOP + scrollPaddingTop)
      window.scroll({
        top: scrollPoint,
        behavior: 'smooth',
      })
    }
  }

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()
  collect({
    type: 'FAQ',
    data: faqs,
  })

  return (
    <>
      <Head>
        <meta name="pageid" content="faqs" />
        <title>{createPageTitle('Frequently Asked Questions')}</title>
        {getIndexTag(isBrgAgent, 'help-centre', handlerKey!)}
      </Head>
      <BlogHero label="" title="How can we help?" style={heroStyle}>
        <BreadCrumbWrapper>
          <Breadcrumb items={breadCrumbData} />
        </BreadCrumbWrapper>
        <WaveDivider curveSize="small" fill="#ffffff" />
      </BlogHero>
      <Spacing size="XXL" />
      <Wrapper>
        <FaqGrid>
          {faqTopics.map(({ icon, label, description }, index) => (
            <Card key={index} onClick={() => handleClickFaq(label)}>
              <Icon name={icon} width={38} height={38} />
              <Spacing size="S" />
              <Heading size="S" spacing={false}>
                {label}
              </Heading>
              <Spacing size="XS" />
              <Description>{description}</Description>
            </Card>
          ))}
        </FaqGrid>
        <Spacing size="XXXL" />
        {faqTopics.map(({ faqs, label }, index) => (
          <div id={label} key={index}>
            <FaqHeader>{label}</FaqHeader>
            <Spacing size="M" />
            {faqs?.map(({ question, answer }, index) => (
              <Accordion key={index} content={answer} anchorColor="brand-secondary">
                <AccordionLabel>{question}</AccordionLabel>
              </Accordion>
            ))}
            <Spacing size="XXXL" />
          </div>
        ))}
        <Spacing size="XXXL" />
        <SupportContainer id="contact-us">
          <SupportSectionLeft>
            <SupportLabel>
              <Heading size="L">Here (almost) always</Heading>
              <Icon name="arrow" />
            </SupportLabel>
            <Spacing size="S" />
            <SupportDescription>
              Have a question about an experience or need help with a booking? Our friendly team is available 363 days a
              year from 9am–6pm AEST/AEDT, for support via phone.
            </SupportDescription>
            <Spacing size="XL" />
            {/* TODO fix Button alternate inverse for exoz <<< */}
            <ButtonContainer>
              {handlerKey !== 'virgin' && (
                <Button size="S" alternate onClick={() => initChat(0, partnerCode)} dataTestId="faqs-live-chat">
                  Live chat
                </Button>
              )}
              <Button
                disabled
                size="S"
                inverse
                onClick={() => (window.location.href = 'mailto:info@experienceoz.com.au')}
              >
                Email Support
              </Button>
            </ButtonContainer>
          </SupportSectionLeft>
          <Spacing size="XXL" />
          <SupportSectionRight>
            <SupportContactContainer>
              <BlackCard />
              <YellowCard>
                <SupportContact>
                  <Icon name="phoneRinging" width={30} height={30} />
                  <Spacing size="S" />
                  <Heading size="S">Contact Customer Support</Heading>
                  <Spacing size="XS" />
                  <ContactNumber>
                    <ContactName>Australia</ContactName>: 1300 935 532
                  </ContactNumber>
                  <Spacing size="XS" />
                  <ContactNumber>
                    <ContactName>New Zealand</ContactName>: 09 887 4665
                  </ContactNumber>
                  <Spacing size="XS" />
                  <ContactNumber>
                    <ContactName>Email</ContactName>: info@experienceoz.com.au
                  </ContactNumber>
                </SupportContact>
              </YellowCard>
            </SupportContactContainer>
          </SupportSectionRight>
        </SupportContainer>
        <Spacing size="XXXL" />
      </Wrapper>
    </>
  )
}

export default HelpCentrePageTemplate
